import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const buttonActive = cn("active:translate-y-0.5 active:shadow-none");
const buttonShadows = cn("shadow-xs hover:shadow-xs-hover");

const buttonVariants = cva(
  `p-2.5 px-4 gap-2 rounded-md
  inline-flex items-center justify-start
  whitespace-nowrap font-medium focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring
  disabled:pointer-events-none disabled:opacity-50
  [&_svg]:size-6 [&_svg]:shrink-0
  hover:bg-black/5
  cursor-pointer
  data-[is-active=true]:bg-primary/5
  data-[is-active=true]:text-primary
  data-[is-active=true]:hover:bg-primary/10
  data-[is-active=true]:border-primary/30
  group`,
  {
    variants: {
      variant: {
        breadcrumb: cn(
          "bg-transparent p-0 hover:bg-transparent active:bg-transparent hover:text-primary",
          "p-0",
          buttonActive,
        ),
        nav: cn(
          "[&_svg]:text-gray-600 [&_svg]:group-hover:text-foreground [&_svg]:stroke-[1.5px] py-2 bg-transparent hover:bg-black/5 text-foreground border border-transparent data-[is-active=true]:border-primary data-[is-active=true]:border-l-4 data-[is-active=true]:border-t-0 data-[is-active=true]:border-r-0 data-[is-active=true]:border-b-0 data-[is-active=true]:border-primary data-[is-active=true]:text-foreground font-thin data-[is-active=true]:active:bg-primary/30 data-[is-active=true]:hover:bg-primary/20  data-[is-active=true]:bg-primary/10 shadow-none",
          buttonActive,
        ),
        default: cn(
          "bg-white text-primary hover:bg-[#f0f0fe] border border-border active:bg-[#f0f0fe]  data-[is-active=true]:active:bg-primary",
          buttonShadows,
          buttonActive,
        ),
        active: cn(
          "border border-primary text-primary bg-background hover:text-primary border-primary-hover/30 data-[is-active=true]:active:bg-primary-active",
          buttonShadows,
          buttonActive,
        ),
        warn: cn(
          "bg-amber-50 text-amber-700 hover:bg-amber-100 border border-amber-200/50 active:bg-amber-200 data-[is-active=true]:active:bg-amber-300",
          buttonShadows,
          buttonActive,
        ),
        green: cn(
          "bg-teal-500 text-white  hover:bg-teal-600 border border-teal-600/50 active:bg-teal-600 data-[is-active=true]:active:bg-teal-600",
          buttonShadows,
          buttonActive,
        ),
        bold: cn(
          "bg-primary text-primary-foreground hover:bg-primary-hover border border-primary-hover/50 active:bg-primary-active data-[is-active=true]:active:bg-primary-active",
          buttonShadows,
          buttonActive,
        ),
        black: cn(
          "bg-foreground text-white hover:bg-black border border-foreground/50 active:bg-black data-[is-active=true]:active:bg-black",
          buttonShadows,
          buttonActive,
        ),
        destructive: cn(
          "bg-destructive text-destructive-foreground hover:bg-destructive/90",
          buttonShadows,
          buttonActive,
        ),
        outline: cn(
          "border bg-background hover:text-accent-foreground border-border bg-linear-to-b from-muted/20 to-muted/50",
          "data-[is-active=true]:bg-linear-to-b",
          "data-[is-active=true]:from-primary/10",
          "data-[is-active=true]:to-primary/15",
          buttonShadows,
          buttonActive,
        ),
        secondary: cn("bg-secondary text-secondary-foreground hover:bg-secondary/80", buttonShadows, buttonActive),
        ghost: cn(
          "data-[is-active=true]:text-foreground data-[is-active=true]:hover:text-primary data-[is-active=true]:active:text-primary hover:bg-secondary active:bg-black/7 hover:text-primary",
          buttonActive,
        ),
        dropdownMenuTrigger: cn(
          "text-primary",
          "data-[is-active=true]:hover:text-primary data-[is-active=true]:active:text-primary hover:bg-secondary active:bg-black/7 hover:text-primary",
          "data-[is-active=true]:bg-secondary data-[is-active=true]:text-primary",
          buttonActive,
        ),
        link: "text-primary underline-offset-4 hover:decoration-none",
        subduedLink:
          "text-muted-foreground underline-offset-4 hover:underline hover:bg-transparent active:text-primary",
        textOnly: "w-fit text-foreground bg-transparent hover:bg-transparent active:bg-none p-0 py-0 px-0",
      },
      size: {
        default: "text-base",
        sm: "text-sm py-1 px-3 rounded-md [&_svg]:size-4 [&_svg]:mr-0 [&_svg]:ml-0",
        xs: "text-xs py-1 px-2 rounded-md [&_svg]:size-4 [&_svg]:mr-0 [&_svg]:ml-0",
        md: "text-md",
        lg: "text-lg",
        xl: "text-xl",
        "2xl": "text-2xl",
        "3xl": "text-3xl",
        icon: "[&_svg]:size-auto [&_svg]:mr-0 [&_svg]:ml-0 h-11 w-14 justify-center items-center",
        iconSm: "[&_svg]:size-6 [&_svg]:mr-0 [&_svg]:ml-0 h-12 w-12 justify-center items-center",
        iconXs: "[&_svg]:size-4 [&_svg]:mr-0 [&_svg]:ml-0 h-8 w-8 justify-center items-center",
        input: "text-sm py-1 px-3 rounded-md h-11 [&_svg]:size-4 [&_svg]:mr-0 [&_svg]:ml-0",
      },
      justify: {
        start: "justify-start",
        center: "justify-center",
        end: "justify-end",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
      justify: "center",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
  VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  isActive?: boolean;
  shineColorRgba?: string;
  animationDuration?: number;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      shineColorRgba,
      animationDuration,
      justify,
      asChild = false,
      isActive = false,
      ...props
    },
    ref,
  ) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, justify, className }), shineColorRgba && "animate-shine")}
        style={
          shineColorRgba
            ? {
              backgroundImage: `linear-gradient(120deg, transparent 15%, ${shineColorRgba} 25%, transparent 35%)`,
              backgroundSize: "200% 100%",
              backgroundRepeat: "no-repeat",
              animationDuration: `${animationDuration ?? 4}s`,
            }
            : undefined
        }
        data-is-active={isActive}
        ref={ref}
        {...props}
      />
    );
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
